import { useCallback } from 'react';
import api from '@/lib/api';
import { useBlockStore } from '@/store/blockStore';
import type { Block } from '@/types';
import { BlockType } from '@/types';
import type { TiptapQuoteEditorHandle } from '@/components/editor/TiptapQuoteEditor';

type BlockStoreState = ReturnType<typeof useBlockStore.getState>;

interface UseAIBlockHandlersParams {
  blocks: Block[];
  quoteId: string;
  setBlocks: BlockStoreState['setBlocks'];
  updateBlock: BlockStoreState['updateBlock'];
  tiptapEditorRef: React.MutableRefObject<TiptapQuoteEditorHandle | null>;
}

/** Handlers waarmee de AI-chat pagina's kan aanmaken, verwijderen en verplaatsen. */
export function useAIBlockHandlers({
  blocks,
  quoteId,
  setBlocks,
  updateBlock,
  tiptapEditorRef,
}: UseAIBlockHandlersParams) {
  const handleAICreateBlock = useCallback(
    async (type: string, content: Record<string, unknown>, insertAfterPage?: number): Promise<{ blockId: string } | null> => {
      try {
        const insertAt = insertAfterPage && insertAfterPage > 0
          ? Math.min(insertAfterPage, blocks.length)
          : blocks.length;

        const newBlock = await api.createBlock(quoteId, {
          type: type as BlockType,
          order: insertAt,
          content: content as Block['content'],
        });

        const tiptapEditor = tiptapEditorRef.current;
        if (tiptapEditor) {
          tiptapEditor.flushSync();
        }

        useBlockStore.getState().pushStructuralSnapshot();
        setBlocks([...blocks.slice(0, insertAt), newBlock, ...blocks.slice(insertAt)], {
          preserveStructuralHistory: true,
        });
        updateBlock(newBlock.id, {
          content: newBlock.content,
          styles: newBlock.styles,
          config: newBlock.config,
          quoteId: newBlock.quoteId,
          type: newBlock.type,
          order: newBlock.order,
        });

        return { blockId: newBlock.id };
      } catch (err) {
        console.error('AI create block failed:', err);
        return null;
      }
    },
    [blocks, quoteId, setBlocks, updateBlock, tiptapEditorRef],
  );

  const handleAIDeleteBlock = useCallback(
    async (pageNumber: number): Promise<boolean> => {
      try {
        const idx = pageNumber - 1;
        if (idx < 0 || idx >= blocks.length) return false;
        const block = blocks[idx];
        tiptapEditorRef.current?.cancelPendingSync();
        useBlockStore.getState().pushStructuralSnapshot();
        await api.deleteBlock(quoteId, block.id);
        setBlocks(blocks.filter((_, i) => i !== idx), { preserveStructuralHistory: true });
        return true;
      } catch (err) {
        console.error('AI delete block failed:', err);
        return false;
      }
    },
    [blocks, setBlocks, quoteId, tiptapEditorRef],
  );

  const handleAIMoveBlock = useCallback(
    async (pageNumber: number, newPosition: number): Promise<boolean> => {
      try {
        const fromIdx = pageNumber - 1;
        const toIdx = newPosition - 1;
        if (fromIdx < 0 || fromIdx >= blocks.length || toIdx < 0 || toIdx >= blocks.length) return false;
        const updated = [...blocks];
        const [moved] = updated.splice(fromIdx, 1);
        updated.splice(toIdx, 0, moved);
        useBlockStore.getState().pushStructuralSnapshot();
        setBlocks(updated, { preserveStructuralHistory: true });
        for (let i = 0; i < updated.length; i++) {
          updateBlock(updated[i].id, { order: i });
        }
        return true;
      } catch (err) {
        console.error('AI move block failed:', err);
        return false;
      }
    },
    [blocks, setBlocks, updateBlock],
  );

  return { handleAICreateBlock, handleAIDeleteBlock, handleAIMoveBlock };
}
